import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TableContainer,
  Paper,
  Box,
} from "@material-ui/core";

const RivalryIndexTable = ({ data }) => {
  // Dynamically determine teams from the data
  const teams = useMemo(() => {
    const teamSet = new Set();
    if (Array.isArray(data)) {
      data.forEach((item) => {
        teamSet.add(item.team1.trim());
        teamSet.add(item.team2.trim());
      });
    }
    // Sort teams alphabetically for consistent plotting order
    return Array.from(teamSet).sort();
  }, [data]);

  //  Build a lookup map for both directions (based on the derived teams)
  const rivalryMap = useMemo(() => {
    const map = {};
    if (Array.isArray(data)) {
      data.forEach(({ team1, team2, rivalryIndex }) => {
        const t1 = team1.trim();
        const t2 = team2.trim();
        if (!map[t1]) map[t1] = {};
        if (!map[t2]) map[t2] = {};

        // Store both directions (t1 -> t2 and t2 -> t1)
        map[t1][t2] = rivalryIndex;
        map[t2][t1] = rivalryIndex;
      });
    }
    return map;
  }, [data]); // data is the dependency

  // Added defensive checks for data in RivalryPage
  if (teams.length === 0) {
    return (
      <Box p={2} style={{ textAlign: "center" }}>
        <Typography color="textSecondary">
          No rivalry data available.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      style={{
        marginTop: 6,
        overflowX: "auto",
        overflowY: "auto",
        maxWidth: "100%", // Ensure it fits the parent container
      }}
    >
      <Table
        style={{
          borderCollapse: "collapse",
        }}
      >
        <TableHead>
          <TableRow>
            {/* Empty top-left cell */}
            <TableCell
              style={{ border: "0px", width: "1%", padding: "0 8px" }} // Minimized width
            ></TableCell>
            {/* Column Headers */}
            {teams.map((team) => (
              <TableCell
                key={team}
                align="center"
                style={{
                  fontWeight: "bold",
                  border: "0px",
                  padding: "8px 4px",
                }}
              >
                {team}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {teams.map((rowTeam) => (
            <TableRow key={rowTeam}>
              {/* Left-side header cell (Row Label) */}
              <TableCell
                style={{
                  fontWeight: "bold",
                  border: "0px",
                  whiteSpace: "nowrap",
                  padding: "8px 4px",
                  textAlign: "right", // Aligns text closer to the grid
                }}
              >
                {rowTeam}
              </TableCell>

              {/* Table cells (The Heatmap) */}
              {teams.map((colTeam) => {
                const isDiagonal = rowTeam === colTeam;

                // Lookup value
                const value = isDiagonal
                  ? 0
                  : rivalryMap[rowTeam]?.[colTeam] ||
                    rivalryMap[colTeam]?.[rowTeam] ||
                    0;

                const displayValue = isDiagonal ? 0.0 : value;
                const bgColor = getHeatmapColor(displayValue);

                // Determine text color based on background darkness
                // Values >= 3.5 are generally dark red/orange, needing white text
                const textColor = value > 3.5 ? "white" : "black";

                return (
                  <TableCell
                    key={`${rowTeam}-${colTeam}`}
                    align="center"
                    style={{
                      border: "1px solid white",
                      textAlign: "center",
                      fontWeight: 600,
                      padding: "8px 4px",
                      backgroundColor: bgColor,
                      color: textColor,
                      width: "10%",
                      height: "40px",
                    }}
                  >
                    {/* Display value with one decimal place */}
                    {displayValue.toFixed(1)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default function RivalryPage({ data }) {
  // Use the actual screenshot data structure for display
  let teamData = data?.data?.rivalryIndexDtoList || [];

  return (
    <>
      <Box p={2} overflow="auto">
        <RivalryIndexTable data={teamData} />
        {/* Placeholder for the color legend (bar at the bottom of the screenshot) */}
      </Box>
      {teamData?.length > 0 && (
        <Box style={{ textAlign: "center", paddingTop: 24 }}>
          {/* You would typically use a library like D3 or Chart.js to render this gradient bar */}
          <Box style={{ textAlign: "center" }}>
            {/* This is a simple placeholder Box to visually represent the gradient bar */}
            <Box
              style={{
                height: 10,
                width: "60%",
                margin: "8px auto 0",
                borderRadius: 5,
                // Using CSS gradient to simulate the color range shown in the bar
                background:
                  "linear-gradient(to right, #83D0EC, #F46A68, #E25554, #AD2934, #8B0000)",
              }}
            />
            <Box
              display="flex"
              justifyContent="space-between"
              width="60%"
              margin="auto"
            >
              <Typography variant="caption" color="textSecondary">
                0
              </Typography>
              <Typography variant="caption" color="textSecondary">
                5
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

// ---  Color Gradient Function ---
// The scale is 0 to 5. We use HSL or RGB interpolation to match the screenshot colors.
const getHeatmapColor = (value) => {
  // Normalize value to 0-5
  const v = Math.min(Math.max(value, 0), 5.0);

  if (v === 0.0) {
    return "#83D0EC"; // Light Blue
  } else if (v <= 1.5) {
    return "#F46A68"; // Light Red
  } else if (v <= 2.5) {
    return "#E25554"; // Medium Red
  } else if (v <= 4.5) {
    return "#AD2934"; // Dark Red
  } else {
    // v > 4.5
    return "#8B0000"; // Darker Red
  }
};
